import { Component } from '@theme/component';

/**
 * A custom element that manages mobile menu accordion functionality.
 * Handles single-level menu structure: parent links with direct sublinks.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The accordion button/header
 * @property {HTMLElement} panel - The accordion content panel
 *
 * @extends {Component<Refs>}
 */
class MobileMenuAccordion extends Component {
  requiredRefs = ['button', 'panel'];

  connectedCallback() {
    super.connectedCallback();

    // Ensure we're using direct child refs, not nested component refs
    // Find the button and panel that are direct children of this component
    const directButton = this.querySelector(':scope > [ref="button"]');
    const directPanel = this.querySelector(':scope > [ref="panel"]');
    
    // Override refs if we found direct children that differ from what Component found
    if (directButton && directButton !== this.refs.button) {
      this.refs.button = directButton;
    }
    if (directPanel && directPanel !== this.refs.panel) {
      this.refs.panel = directPanel;
    }

    // Add class to panel for styling
    if (this.refs.panel) {
      this.refs.panel.classList.add('mobile-menu-accordion__panel');
    }

    // Set initial ARIA attributes
    this.#updateAriaAttributes();

    // Add click handler to button
    if (this.refs.button) {
      this.refs.button.addEventListener('click', this.#handleClick);
      // Add keyboard handler
      this.refs.button.addEventListener('keydown', this.#handleKeyDown);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.button.removeEventListener('click', this.#handleClick);
    this.refs.button.removeEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * Get whether the accordion is open
   * @returns {boolean}
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * Update ARIA attributes based on current state
   */
  #updateAriaAttributes() {
    const isExpanded = this.isOpen;
    this.refs.button.setAttribute('aria-expanded', isExpanded.toString());
    
    if (this.refs.panel.id) {
      this.refs.button.setAttribute('aria-controls', this.refs.panel.id);
    }
  }

  /**
   * Handle click event on accordion button
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    // Prevent navigation if this is a link
    if (this.refs.button.tagName === 'A') {
      event.preventDefault();
    }

    // Stop event propagation to prevent parent accordions from toggling
    event.stopPropagation();

    this.toggle();
  };

  /**
   * Handle keyboard events
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.toggle();
    } else if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      this.refs.button.focus();
    }
  };

  /**
   * Toggle the accordion open/closed
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the accordion
   */
  open() {
    this.setAttribute('open', '');
    this.#updateAriaAttributes();
  }

  /**
   * Close the accordion
   */
  close() {
    this.removeAttribute('open');
    this.#updateAriaAttributes();
  }
}

if (!customElements.get('mobile-menu-accordion')) {
  customElements.define('mobile-menu-accordion', MobileMenuAccordion);
}

